/**
 * Which tables a table-camera phone films.
 * Every covered table's seated players receive the captured video.
 * The main table is the one shown first (presence and the coach list).
 */

/** Mirrors Backend tableCameraRegistry MAX_TABLES_PER_PHONE. */
export const MAX_TABLES_PER_PHONE = 12;

function toTable(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function uniqueSorted(list) {
  return [...new Set(list)].sort((a, b) => a - b);
}

/**
 * @returns {{ main: number|null, tables: number[] }} tables ascending, main included.
 */
export function normalizeTablePhoneCoverage(main, tables) {
  const list = (Array.isArray(tables) ? tables : [])
    .map(toTable)
    .filter((n) => n != null);
  const mainTable = toTable(main);
  if (mainTable != null) list.push(mainTable);
  const covered = uniqueSorted(list).slice(0, MAX_TABLES_PER_PHONE);
  return {
    main: mainTable != null && covered.includes(mainTable)
      ? mainTable
      : covered[0] ?? null,
    tables: covered,
  };
}

/** Tables this phone films right now (empty when table-phone mode is off). */
export function tablePhoneCoveredTables(settings) {
  if (settings?.tablePhoneEnabled !== true) return [];
  return normalizeTablePhoneCoverage(
    settings?.tablePhoneNumber,
    settings?.tablePhoneNumbers
  ).tables;
}

function withCoverage(settings, main, tables) {
  const on = tables.length > 0;
  return {
    ...settings,
    tablePhoneEnabled: on,
    stationPhoneEnabled: on ? false : settings?.stationPhoneEnabled === true,
    tablePhoneNumber: on ? main : null,
    tablePhoneNumbers: tables,
  };
}

/**
 * Tap a table: add it to (or drop it from) this phone's coverage.
 * Picking the first table turns table-phone mode on; dropping the last one
 * turns it off. Dropping the main table hands "main" to the next table.
 */
export function toggleTablePhoneTable(settings, tableNumber) {
  const n = toTable(tableNumber);
  if (n == null) return settings;
  const covered = tablePhoneCoveredTables(settings);
  const currentMain =
    covered.length > 0
      ? normalizeTablePhoneCoverage(settings?.tablePhoneNumber, covered).main
      : null;
  if (covered.includes(n)) {
    const tables = covered.filter((x) => x !== n);
    const main = currentMain === n ? tables[0] ?? null : currentMain;
    return withCoverage(settings, main, tables);
  }
  if (covered.length >= MAX_TABLES_PER_PHONE) return settings;
  const tables = uniqueSorted([...covered, n]);
  return withCoverage(settings, currentMain ?? n, tables);
}

/** Make `tableNumber` the main table (adds it to coverage if needed). */
export function setTablePhoneMainTable(settings, tableNumber) {
  const n = toTable(tableNumber);
  if (n == null) return settings;
  const covered = tablePhoneCoveredTables(settings);
  if (!covered.includes(n) && covered.length >= MAX_TABLES_PER_PHONE) {
    return settings;
  }
  return withCoverage(settings, n, uniqueSorted([...covered, n]));
}

/** Cover every listed table (keeps the current main when still listed). */
export function setTablePhoneTables(settings, tableNumbers) {
  const covered = tablePhoneCoveredTables(settings);
  const { tables } = normalizeTablePhoneCoverage(null, tableNumbers);
  const keepMain = normalizeTablePhoneCoverage(
    settings?.tablePhoneNumber,
    covered
  ).main;
  const main = tables.includes(keepMain) ? keepMain : tables[0] ?? null;
  return withCoverage(settings, main, tables);
}

/**
 * Stamp a table-phone clip so players on every filmed table can open it.
 * `tableNumber` is the table this recording is filed under; `tableNumbers`
 * is the full calibration selection.
 */
export function tablePhoneShareFields(tableNumber, tableNumbers) {
  const recorded = toTable(tableNumber);
  const { tables } = normalizeTablePhoneCoverage(recorded, tableNumbers);
  if (!tables.length) return null;
  return {
    table_phone: true,
    table_number:
      recorded != null && tables.includes(recorded) ? recorded : tables[0],
    table_numbers: tables,
  };
}

/** Unique, ascending table numbers from session table rows. */
export function tableNumbersFromSessionTables(sessionTables) {
  return uniqueSorted(
    (Array.isArray(sessionTables) ? sessionTables : [])
      .map((row) => toTable(row?.table_number))
      .filter((n) => n != null)
  );
}
